/**
 * SharePage — public read-only view for a shared RCA.
 *
 * Route: /share/:token
 * Auth: NONE — the token in the path IS the credential. The Authorization
 *       header must NOT be sent; we use `publicApi` (a separate axios instance
 *       without the request interceptor) so the JWT interceptor in ApiHelper
 *       is never involved.
 *
 * Sections rendered (only when present):
 *   Header:    title · target_type badge · shared_by · created_at · expires_at
 *   Action:    "Copy claude command" (ContentCopy → Check micro-interaction)
 *   Body:
 *     [analysis]     summary → impact → fix snippet (code block, syntax-coloured)
 *     [investigation] narrative (NarrativeMarkdown)
 *     evidence:      sentry_permalink · file_refs · commits · stack_trace (collapsed)
 *
 * 404 handling: inline empty state; does NOT redirect to /login.
 * Loading state: skeleton blocks per section; no spinner.
 * Accessibility: aria-live polite region for copy feedback, aria-label on icon
 *   buttons, reduced-motion guard on copy icon transition, focus management.
 *
 * Aesthetic mirrors IssueDetailPage / InvestigationDetailPage:
 *   JetBrains Mono headers · amber primary · retro uppercase labels · 8dp rhythm.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Alert,
  Box,
  Chip,
  Collapse,
  Container,
  Divider,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useReducedMotion } from '@/hooks/useReducedMotion';

import { publicShare } from '@/helpers/ApiHelper';
import { NarrativeMarkdown } from '@/components/NarrativeMarkdown';
import type { SharedRca, ShareTargetType } from '@/types/share';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmtDate(iso: string): string {
  try {
    const d = new Date(iso);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())} UTC`;
  } catch {
    return iso;
  }
}

function formatRelativeExpiry(iso: string): string {
  const expires = new Date(iso).getTime();
  const now = Date.now();
  const ms = expires - now;
  if (ms <= 0) return 'expired';
  const day = 24 * 60 * 60 * 1000;
  const days = Math.floor(ms / day);
  if (days === 0) {
    const hours = Math.max(1, Math.floor(ms / (60 * 60 * 1000)));
    return `expires in ${hours} hour${hours === 1 ? '' : 's'}`;
  }
  return `expires in ${days} day${days === 1 ? '' : 's'}`;
}

function targetTypeBadge(type: ShareTargetType) {
  return (
    <Chip
      size="small"
      label={type}
      variant="outlined"
      sx={{
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: 11,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Section label (retro uppercase monospace heading)
// ---------------------------------------------------------------------------

function SectionLabel({ children }: { children: string }) {
  return (
    <Typography
      component="h2"
      sx={{
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: '0.75rem',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color: 'text.secondary',
        mb: 1,
      }}
    >
      {children}
    </Typography>
  );
}

// ---------------------------------------------------------------------------
// Copy button — ContentCopy → Check icon swap micro-interaction
// ---------------------------------------------------------------------------

interface CopyButtonProps {
  text: string;
  label: string;
  /** aria-live node setter to announce copy result */
  announce: (msg: string) => void;
}

function CopyButton({ text, label, announce }: CopyButtonProps) {
  const reduceMotion = useReducedMotion();
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleClick = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      announce(`${label} copied`);
      const holdMs = reduceMotion ? 600 : 1500;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => setCopied(false), holdMs);
    } catch {
      announce(`Could not copy ${label.toLowerCase()}`);
    }
  }, [text, label, announce, reduceMotion]);

  useEffect(() => () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  }, []);

  return (
    <Box
      component="button"
      aria-label={`Copy ${label}`}
      onClick={() => void handleClick()}
      sx={{
        appearance: 'none',
        background: 'transparent',
        border: '1px solid',
        borderColor: copied ? 'success.main' : 'divider',
        borderRadius: 1,
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        px: 2,
        py: 1,
        minHeight: 44,
        minWidth: 44,
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: '0.8125rem',
        fontWeight: 600,
        color: copied ? 'success.main' : 'primary.main',
        transition: 'color 200ms ease, border-color 200ms ease',
        '&:hover': {
          bgcolor: 'rgba(255,255,255,0.04)',
        },
        '&:focus-visible': {
          outline: '2px solid',
          outlineColor: 'primary.main',
          outlineOffset: 2,
        },
      }}
    >
      {copied ? (
        <CheckIcon fontSize="small" />
      ) : (
        <ContentCopyIcon fontSize="small" />
      )}
      <span>Copy claude command</span>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Fix snippet — monospace code block, syntax-friendly
// ---------------------------------------------------------------------------

function FixSnippet({ snippet }: { snippet: string }) {
  return (
    <Box
      component="pre"
      sx={{
        m: 0,
        p: 2,
        bgcolor: 'background.elev2',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'auto',
        fontFamily: '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
        fontSize: '0.8125rem',
        lineHeight: 1.55,
        whiteSpace: 'pre',
        color: 'text.primary',
      }}
    >
      <Box component="code">{snippet}</Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Stack trace — collapsed by default
// ---------------------------------------------------------------------------

function StackTraceBlock({ trace }: { trace: string }) {
  const [expanded, setExpanded] = useState(false);
  const theme = useTheme();
  const reduceMotion = useReducedMotion();

  return (
    <Box>
      <Box
        component="button"
        aria-expanded={expanded}
        onClick={() => setExpanded((v) => !v)}
        sx={{
          appearance: 'none',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 0,
          py: 1,
          minHeight: 44,
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: '0.75rem',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          color: theme.palette.primary.main,
          '&:focus-visible': {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: 2,
          },
        }}
      >
        <Typography component="span" sx={{ color: 'primary.main', fontFamily: 'inherit', fontSize: 'inherit', userSelect: 'none' }} aria-hidden="true">
          {expanded ? '▼' : '▶'}
        </Typography>
        STACK TRACE
      </Box>
      <Collapse
        in={expanded}
        timeout={reduceMotion ? 0 : theme.transitions.duration.standard}
        unmountOnExit={false}
      >
        <Box
          component="pre"
          sx={{
            m: 0,
            mt: 1,
            p: 2,
            bgcolor: 'background.elev2',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            overflow: 'auto',
            fontFamily: '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
            fontSize: '0.75rem',
            lineHeight: 1.5,
            whiteSpace: 'pre',
            color: 'text.secondary',
          }}
        >
          <Box component="code">{trace}</Box>
        </Box>
      </Collapse>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Loading skeleton
// ---------------------------------------------------------------------------

function SharePageSkeleton() {
  return (
    <Container maxWidth="md" sx={{ py: { xs: 3, sm: 4 } }}>
      <Stack spacing={3}>
        {/* Header skeleton */}
        <Box>
          <Skeleton variant="text" width="60%" height={32} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="40%" height={20} />
        </Box>

        {/* Action button skeleton */}
        <Skeleton variant="rectangular" height={44} width={220} sx={{ borderRadius: 1 }} />

        <Divider />

        {/* Body section skeletons */}
        <Box>
          <Skeleton variant="text" width="15%" height={16} sx={{ mb: 1 }} />
          <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 1 }} />
        </Box>

        <Box>
          <Skeleton variant="text" width="12%" height={16} sx={{ mb: 1 }} />
          <Skeleton variant="rectangular" height={64} sx={{ borderRadius: 1 }} />
        </Box>

        <Box>
          <Skeleton variant="text" width="18%" height={16} sx={{ mb: 1 }} />
          <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 1 }} />
        </Box>
      </Stack>
    </Container>
  );
}

// ---------------------------------------------------------------------------
// 404 / expired empty state
// ---------------------------------------------------------------------------

function ShareNotFound() {
  return (
    <Container maxWidth="sm" sx={{ py: { xs: 6, sm: 8 } }}>
      <Stack spacing={2} alignItems="center" sx={{ textAlign: 'center' }}>
        <Typography
          component="h1"
          sx={{
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: { xs: '1rem', sm: '1.125rem' },
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            color: 'primary.main',
          }}
        >
          ▸ SHARE LINK NOT FOUND
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65, maxWidth: 420 }}>
          This share link could not be resolved. It may have expired, been revoked by the
          owner, or never existed. If you received this link from a colleague, ask them to
          mint a fresh one from the Sherlock issue or investigation page.
        </Typography>
        <Typography
          variant="caption"
          sx={{
            fontFamily: '"JetBrains Mono", monospace',
            color: 'text.disabled',
            mt: 1,
          }}
        >
          error: 404 / token unresolvable
        </Typography>
      </Stack>
    </Container>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function SharePage() {
  const { token } = useParams<{ token: string }>();

  const [data, setData] = useState<SharedRca | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  // aria-live region for copy announcements (screen readers)
  const [copyAnnouncement, setCopyAnnouncement] = useState<string | null>(null);

  const announce = useCallback((msg: string) => setCopyAnnouncement(msg), []);

  useEffect(() => {
    if (!token) {
      setNotFound(true);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setNotFound(false);
    setFetchError(null);

    publicShare
      .getByToken(token)
      .then((rca) => {
        if (!cancelled) {
          setData(rca);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          // 404 = unknown / revoked / expired — show the inline empty state
          if (err && typeof err === 'object' && 'status' in err && (err as { status: number }).status === 404) {
            setNotFound(true);
          } else {
            const msg = err instanceof Error ? err.message : String(err);
            setFetchError(msg);
          }
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  if (isLoading) {
    return <SharePageSkeleton />;
  }

  if (notFound) {
    return <ShareNotFound />;
  }

  if (fetchError) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error" role="alert">
          Failed to load shared RCA: {fetchError}
        </Alert>
      </Container>
    );
  }

  if (!data) return null;

  // Derive the prompt URL: backend base + /api/share/rca/{token}/prompt
  const backendBase = (import.meta.env.VITE_API_URL as string | undefined) ?? '';
  const promptUrl = `${backendBase}/api/share/rca/${token ?? ''}/prompt`;
  const claudeCommand = `claude "$(curl -s ${promptUrl})"`;

  return (
    <Container
      maxWidth="md"
      sx={{ py: { xs: 3, sm: 4 }, px: { xs: 2, sm: 3 } }}
    >
      {/* Visually-hidden aria-live region for copy feedback */}
      <Box
        role="status"
        aria-live="polite"
        sx={{
          position: 'absolute',
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: 'hidden',
          clip: 'rect(0,0,0,0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      >
        {copyAnnouncement}
      </Box>

      <Stack spacing={3}>
        {/* ================================================================
            HEADER
        ================================================================ */}
        <Box>
          {/* Title + target type badge */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={1.5}
            sx={{ mb: 1.5 }}
          >
            <Typography
              component="h1"
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: { xs: '1rem', sm: '1.25rem' },
                fontWeight: 700,
                lineHeight: 1.3,
                wordBreak: 'break-word',
                flexGrow: 1,
              }}
            >
              {data.title}
            </Typography>
            {targetTypeBadge(data.target_type)}
          </Stack>

          {/* Meta strip: shared_by · dates */}
          <Box
            sx={{
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: 12,
              color: 'text.secondary',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.5,
              lineHeight: 1.8,
            }}
          >
            <Box component="span">shared by {data.shared_by.name}</Box>
            <Box component="span" sx={{ color: 'divider' }}> · </Box>
            <Box component="span" sx={{ color: 'text.disabled' }}>{data.shared_by.email}</Box>
            <Box component="span" sx={{ color: 'divider' }}> · </Box>
            <Box component="span">{fmtDate(data.created_at)}</Box>
            <Box component="span" sx={{ color: 'divider' }}> · </Box>
            <Box
              component="span"
              sx={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {formatRelativeExpiry(data.expires_at)}
            </Box>
          </Box>
        </Box>

        {/* ================================================================
            PRIMARY ACTION: Copy claude command
        ================================================================ */}
        <Box>
          <SectionLabel>CLAUDE CLI</SectionLabel>
          <Stack spacing={1.5}>
            <CopyButton
              text={claudeCommand}
              label="Claude command"
              announce={announce}
            />
            <Box
              sx={{
                p: 1.5,
                bgcolor: 'background.elev2',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              <Typography
                component="code"
                sx={{
                  fontFamily: '"JetBrains Mono", ui-monospace, Menlo, monospace',
                  fontSize: '0.8125rem',
                  color: 'text.secondary',
                  wordBreak: 'break-all',
                }}
              >
                {claudeCommand}
              </Typography>
            </Box>
          </Stack>
        </Box>

        <Divider />

        {/* ================================================================
            BODY — sections rendered only when present
        ================================================================ */}

        {/* SUMMARY (analysis) */}
        {data.summary && (
          <Box>
            <SectionLabel>SUMMARY</SectionLabel>
            <Typography variant="body2" sx={{ lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
              {data.summary}
            </Typography>
          </Box>
        )}

        {/* IMPACT (analysis) */}
        {data.impact && (
          <Box>
            <SectionLabel>IMPACT</SectionLabel>
            <Typography
              variant="body2"
              sx={{ lineHeight: 1.7, color: 'text.secondary', whiteSpace: 'pre-wrap' }}
            >
              {data.impact}
            </Typography>
          </Box>
        )}

        {/* FIX SNIPPET (analysis) */}
        {data.fix_snippet && (
          <Box>
            <SectionLabel>PROPOSED FIX</SectionLabel>
            <FixSnippet snippet={data.fix_snippet} />
          </Box>
        )}

        {/* NARRATIVE (investigation) — markdown-rendered */}
        {data.narrative && (
          <Box>
            <SectionLabel>NARRATIVE</SectionLabel>
            <NarrativeMarkdown text={data.narrative} />
          </Box>
        )}

        {/* EVIDENCE */}
        <EvidenceSection evidence={data.evidence} />
      </Stack>
    </Container>
  );
}

// ---------------------------------------------------------------------------
// Evidence section
// ---------------------------------------------------------------------------

import type { SharedEvidence } from '@/types/share';

function EvidenceSection({ evidence }: { evidence: SharedEvidence }) {
  const hasEvidence =
    evidence.sentry_permalink ||
    evidence.file_refs.length > 0 ||
    evidence.commits.length > 0 ||
    evidence.stack_trace;

  if (!hasEvidence) return null;

  return (
    <Box>
      <SectionLabel>EVIDENCE</SectionLabel>
      <Stack spacing={2}>
        {/* Sentry permalink */}
        {evidence.sentry_permalink && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography
              variant="caption"
              sx={{ fontFamily: '"JetBrains Mono", monospace', color: 'text.disabled', minWidth: 80 }}
            >
              sentry
            </Typography>
            <Box
              component="a"
              href={evidence.sentry_permalink}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: 12,
                color: 'primary.main',
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              open in Sentry <OpenInNewIcon sx={{ fontSize: 12 }} />
            </Box>
          </Stack>
        )}

        {/* File refs */}
        {evidence.file_refs.length > 0 && (
          <Box>
            <Typography
              variant="caption"
              sx={{ fontFamily: '"JetBrains Mono", monospace', color: 'text.disabled', display: 'block', mb: 0.5 }}
            >
              files
            </Typography>
            <Stack spacing={0.5}>
              {evidence.file_refs.map((ref, i) => (
                <Typography
                  key={i}
                  variant="caption"
                  sx={{
                    fontFamily: '"JetBrains Mono", monospace',
                    fontSize: 12,
                    color: 'text.secondary',
                  }}
                >
                  {ref.repo ? `${ref.repo}:` : ''}{ref.path}
                </Typography>
              ))}
            </Stack>
          </Box>
        )}

        {/* Commits */}
        {evidence.commits.length > 0 && (
          <Box>
            <Typography
              variant="caption"
              sx={{ fontFamily: '"JetBrains Mono", monospace', color: 'text.disabled', display: 'block', mb: 0.5 }}
            >
              commits
            </Typography>
            <Stack spacing={0.5}>
              {evidence.commits.map((commit, i) => (
                <Typography
                  key={i}
                  variant="caption"
                  sx={{
                    fontFamily: '"JetBrains Mono", monospace',
                    fontSize: 12,
                    color: 'text.secondary',
                  }}
                >
                  {commit}
                </Typography>
              ))}
            </Stack>
          </Box>
        )}

        {/* Stack trace — collapsed */}
        {evidence.stack_trace && <StackTraceBlock trace={evidence.stack_trace} />}
      </Stack>
    </Box>
  );
}
